<template>
    <div class="welcome-screen" @mousedown="onScreenInteract">
        <!-- Full-bleed sports/gym themed background. -->
        <svg class="ws-bg" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice"
             aria-hidden="true">
            <defs>
                <pattern id="ws-pattern" width="220" height="220" patternUnits="userSpaceOnUse"
                         patternTransform="rotate(18)">
                    <g fill="rgba(255, 255, 255, 0.07)">
                        <!-- Dumbbell -->
                        <rect x="20" y="44" width="9" height="44" rx="3"></rect>
                        <rect x="31" y="52" width="8" height="28" rx="3"></rect>
                        <rect x="39" y="61" width="46" height="10" rx="3"></rect>
                        <rect x="85" y="52" width="8" height="28" rx="3"></rect>
                        <rect x="95" y="44" width="9" height="44" rx="3"></rect>
                        <!-- Ball -->
                        <circle cx="160" cy="150" r="26" fill="none" stroke="rgba(255,255,255,0.08)"
                                stroke-width="4"></circle>
                        <path d="M134 150 H186 M160 124 V176 M141 132 Q160 150 141 168 M179 132 Q160 150 179 168"
                              fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="3"></path>
                    </g>
                </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#ws-pattern)"></rect>
        </svg>

        <div class="ws-content">
            <template v-if="state === 'loading'">
                <div class="ws-state"><i class="fa-solid fa-spinner fa-spin"></i></div>
            </template>

            <template v-else-if="state === 'disabled'">
                <div class="ws-state">{{ $LANG.Get('labels.welcomeScreenDisabled') }}</div>
            </template>

            <template v-else-if="state === 'password'">
                <div class="ws-logo">
                    <svg class="ws-logo-icon" viewBox="0 0 319.52822 247.47235" xmlns="http://www.w3.org/2000/svg"
                         aria-hidden="true">
                        <path :d="logoPath" fill="#ffffff"></path>
                    </svg>
                    <div class="ws-logo-text">Fytlix</div>
                </div>
                <div class="ws-password">
                    <div class="ws-password-label">{{ $LANG.Get('labels.welcomeScreenPasswordPrompt') }}</div>
                    <input ref="passwordInput" v-model="passwordInput" type="password" class="ws-input"
                           @keyup.enter="submitPassword()">
                    <div v-if="passwordError !== ''" class="ws-password-error">{{ passwordError }}</div>
                    <button type="button" class="ws-button" @click="submitPassword()">
                        {{ $LANG.Get('buttons.ok') }}
                    </button>
                </div>
            </template>

            <template v-else-if="state === 'ready'">
                <div class="ws-logo">
                    <svg class="ws-logo-icon" viewBox="0 0 319.52822 247.47235" xmlns="http://www.w3.org/2000/svg"
                         aria-hidden="true">
                        <path :d="logoPath" fill="#ffffff"></path>
                    </svg>
                    <div class="ws-logo-text">Fytlix</div>
                </div>

                <div class="ws-interaction">
                    <div v-if="showingMessage" class="ws-message-wrap">
                        <div class="ws-message" v-html="message"></div>
                        <div class="ws-countdown">{{ countdown }}</div>
                    </div>
                    <input v-else ref="memberInput" v-model="memberInput" class="ws-input ws-input-large"
                           :placeholder="$LANG.Get('labels.welcomeScreenPlaceholder')"
                           @keyup.enter="submit()" @blur="onInputBlur">
                </div>
            </template>
        </div>
    </div>
</template>

<script>
export default {
    name: "WelcomeScreen",
    data() {
        return {
            state: 'loading',
            link: this.$route.params.link,
            password: '',
            passwordInput: '',
            passwordError: '',
            memberInput: '',
            message: '',
            showingMessage: false,
            processing: false,
            countdown: 0,
            countdownTimer: null,
            // Seconds a message stays on screen, configured per club and supplied by the config endpoint.
            timer: 3,
            // Fytlix logo mark (web/assets/img/icon.svg), inlined so this public screen needs no extra assets.
            logoPath: 'm 206.59218,0.87406304 c -22.002,0 -43.99875,-1.2797 -65.97075,-0.71243 -3.2085,0.0829 -21.52575,-1.72821004 -19.03275,5.01509996 1.56225,4.2255 17.91,4.03043 22.0035,4.59953 15.92925,2.21445 39.717,10.2729 53.25,0.62962 3.7275,-2.6559 8.42775,-4.92172 9.75,-9.53181996 m 45,1.14644996 c -34.77975,7.86975 -21.90225,62.5368 12.75,54.70717 35.28375,-7.97242 22.4655,-62.67556 -12.75,-54.70717 m -120.75,19.10355 c -6.84225,-2.02238 -15.38325,-0.75 -22.5,-0.75 H 64.842181 c -2.87737,0 -21.01222,-1.73355 -18.55785,4.34025 1.73362,4.2903 18.8748,3.81022 23.05785,3.90975 18.23025,0.4338 49.697249,11.892 61.499999,-7.5 m 46.5,38.25 c -9.132,9.132 -17.88,18.61875 -27,27.75 -5.8155,5.82225 -12.867,11.51475 -16.98,18.749997 -2.5695,4.5195 -3.1275,9.91725 -2.169,15 0.495,2.62875 2.454,5.55225 1.63425,8.25 -1.236,4.07025 -5.52,7.9335 -8.0745,11.25 -6.378,8.283 -12.3015,17.96475 -19.63575,25.40025 -9.592499,9.7245 -29.888249,1.3815 -39.923699,12.2385 -8.64457,9.35175 -4.59788,25.73925 7.8987,29.16525 7.36425,2.01825 15.81525,0.0315 23.25,-0.6765 15.542999,-1.4805 31.205249,-0.5715 42.682499,-12.8775 7.929,-8.5005 15.1125,-17.9595 22.41975,-27 1.9635,-2.42925 5.39625,-8.6145 9.15075,-7.52475 3.1875,0.9255 6.3795,4.1595 8.997,6.12 3.26625,2.44575 11.3535,6.43575 12.606,10.47225 1.12575,3.62925 -6.03375,10.0725 -8.10375,12.6825 -7.57425,9.552 -20.0565,21.219 -23.67,33 -4.6575,15.18375 10.58325,31.3095 25.91775,24.48825 7.53225,-3.35025 12.1335,-11.43975 16.98825,-17.73825 9.315,-12.084 18.96,-23.91675 28.2735,-36 5.7645,-7.479 11.3895,-15.54075 9.0915,-25.5 -2.75775,-11.9475 -17.877,-20.973 -26.60325,-28.5 -3.4785,-3 -11.35425,-7.04775 -6.6915,-11.87325 5.28375,-5.46825 11.4465,-10.4595 17.1915,-15.44475 1.73025,-1.5015 4.24875,-4.8915 6.75,-4.89 3.0225,7.5e-4 6.31575,4.77375 8.25,6.708 5.79975,5.79975 11.51775,13.5315 18.75,17.601 13.542,7.6185 34.0095,-7.9845 45.75,-14.12325 4.89525,-2.559 10.3545,-4.47225 13.329,-9.48075 5.832,-9.818997 -1.116,-23.111247 -12.579,-23.837247 -8.6805,-0.54975 -18.20475,6.62625 -25.5,10.572 -2.4525,1.326 -7.73175,5.7105 -10.68,4.5915 -3.159,-1.2 -5.778,-7.3845 -7.56975,-10.07325 -5.2125,-7.81875 -10.05525,-15.267 -17.001,-21.6858 -9.987,-9.22898 -19.31325,-21.537 -30.74925,-28.90275 -10.87875,-7.00703 -26.016,-8.6832 -38.25,-12.42998 -6.97125,-2.13495 -15.102,-5.5317 -22.5,-4.23495 -5.89125,1.0326 -10.4205,5.3463 -15,8.84025 -8.80575,6.71813 -21.1245,13.07993 -28.0215,21.7911 -7.89975,9.97755 -0.61425,24.70238 12.2715,23.79938 14.0805,-0.987 22.1805,-19.1481 35.25,-21.63735 5.63175,-1.07258 14.8665,4.2084 20.25,5.9601 m -137.249999,3 c 10.49325,3.13057 24.98602,1.5 36,1.5 4.01775,0 9.81225,0.90585 13.5,-0.89235 2.74275,-1.33748 14.384999,-11.91728 12.617999,-15.1551 -1.4985,-2.7465 -12.110249,-1.20255 -14.867999,-1.20255 -12.6255,0 -23.58675,4.56997 -35.25,9.15217 -4.10332,1.61213 -9.81112,2.52833 -12,6.59783 m 83.999999,18 H 84.342181 c -5.5065,0 -11.8875,-1.2495 -17.25,-0.28275 -7.41278,1.3365 -13.32803,7.1835 -20.25,9.873 -4.77922,1.857 -12.12083,1.58775 -17.25,1.65675 -8.1306,0.11025 -16.66965,0.675 -24.7493999,1.58475 -2.03265,0.22875 -6.14753,1.89375 -4.43579999,4.6815 1.50051999,2.443497 5.78204999,1.983 8.18519999,1.98675 h 23.2499999 44.25 c 12.40125,0 21.633,1.078497 32.249999,-6 5.16375,-3.4425 13.9425,-7.1955 15.75,-13.5 M 37.092181,155.37406 c 1.7784,5.35875 14.7156,3 19.5,3 9.1425,0 23.75925,2.79375 32.25,-0.6435 3.603,-1.45875 6.22875,-4.9575 9,-7.539 2.813999,-2.62125 5.675999,-4.50075 6.749999,-8.3175 -13.457999,0 -32.124749,-3.3765 -44.999999,0.48675 -7.98735,2.39625 -15.16642,9.1095 -22.5,13.01325 z'
        }
    },
    methods: {
        loadConfig(password, passwordAttempt) {
            let vm = this;
            this.$API.APICall('post', 'public/welcome-screen/config/' + encodeURIComponent(this.link), {password: password}, (data, success) => {
                if (!success) {
                    vm.$HELPERS.HandleServerError(vm, data);
                    return;
                }
                if (!data.enabled) {
                    vm.state = 'disabled';
                } else if (data.password_required) {
                    if (passwordAttempt) {
                        vm.passwordError = vm.$LANG.Get('labels.selfRegisterWrongPassword');
                    }
                    vm.state = 'password';
                    vm.focusPassword();
                } else {
                    vm.password = password;
                    if (data.timer > 0) {
                        vm.timer = data.timer;
                    }
                    vm.state = 'ready';
                    vm.focusInput();
                }
            });
        },
        submitPassword() {
            this.passwordError = '';
            this.loadConfig(this.passwordInput, true);
        },
        submit() {
            // A bare ENTER with nothing typed is ignored, as is anything while a message is on screen.
            if (this.processing || this.showingMessage) {
                return;
            }
            let value = this.memberInput.trim();
            if (value === '') {
                return;
            }

            this.processing = true;
            let vm = this;
            this.$API.APICall('post', 'public/welcome-screen/checkin/' + encodeURIComponent(this.link),
                {password: this.password, member_id: value}, (data, success) => {
                    if (success && data && data.message) {
                        vm.displayMessage(data.message);
                    } else {
                        // Anything unexpected (screen disabled mid-session, etc.): quietly reset for the next person.
                        vm.finishCheckIn();
                    }
                }, false);
        },
        displayMessage(message) {
            // Hide the input and show the message in its place for the club's configured number of seconds, then
            // finish the check-in. A visible countdown tells the member how long the message stays up.
            this.message = message;
            this.showingMessage = true;
            this.countdown = this.timer;
            this.countdownTimer = setInterval(() => {
                this.countdown--;
                if (this.countdown <= 0) {
                    this.finishCheckIn();
                }
            }, 1000);
        },
        finishCheckIn() {
            if (this.countdownTimer !== null) {
                clearInterval(this.countdownTimer);
                this.countdownTimer = null;
            }
            this.showingMessage = false;
            this.message = '';
            this.memberInput = '';
            this.processing = false;
            this.focusInput();
        },
        focusInput() {
            this.$nextTick(() => {
                if (this.$refs.memberInput) {
                    this.$refs.memberInput.focus();
                }
            });
        },
        focusPassword() {
            this.$nextTick(() => {
                if (this.$refs.passwordInput) {
                    this.$refs.passwordInput.focus();
                }
            });
        },
        onInputBlur() {
            // The kiosk input must always hold focus so the member can just start typing. Re-focus unless a
            // message is currently showing (the input is hidden then).
            setTimeout(() => {
                if (this.state === 'ready' && !this.showingMessage) {
                    this.focusInput();
                }
            }, 0);
        },
        onScreenInteract() {
            // Tapping/clicking anywhere returns focus to the input.
            if (this.state === 'ready' && !this.showingMessage) {
                this.focusInput();
            }
        }
    },
    mounted() {
        document.title = 'Fytlix';
        this.loadConfig('', false);
    },
    beforeUnmount() {
        if (this.countdownTimer !== null) {
            clearInterval(this.countdownTimer);
        }
    }
}
</script>

<style scoped>
.welcome-screen {
    position: fixed;
    inset: 0;
    overflow: hidden;
    background: linear-gradient(160deg, #fb5d18 0%, #f65221 55%, #c9370f 100%);
    display: flex;
    align-items: stretch;
    justify-content: center;
    font-family: 'Sora', sans-serif;
}

.ws-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.ws-content {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 720px;
    padding: 6vh 24px 24px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* ---------- Logo ---------- */
.ws-logo {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 8vh;
}

.ws-logo-icon {
    width: 128px;
    height: auto;
    filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.2));
}

.ws-logo-text {
    margin-top: 12px;
    color: #ffffff;
    font-family: 'Sora', sans-serif;
    font-weight: 700;
    font-size: 46px;
    letter-spacing: 1px;
    text-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* ---------- Interaction ---------- */
.ws-interaction {
    width: 100%;
    max-width: 520px;
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ws-input {
    width: 100%;
    box-sizing: border-box;
    border: none;
    border-radius: 14px;
    padding: 16px 20px;
    font-family: 'Sora', sans-serif;
    font-size: 20px;
    color: #2f3545;
    background: #ffffff;
    box-shadow: 0 14px 34px -10px rgba(0, 0, 0, 0.35);
    outline: none;
}

.ws-input-large {
    text-align: center;
    font-size: 30px;
    padding: 22px 24px;
    letter-spacing: 2px;
}

.ws-message-wrap {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    animation: ws-fade 0.25s ease;
}

.ws-message {
    width: 100%;
    text-align: center;
    color: #ffffff;
    font-family: 'Sora', sans-serif;
    font-weight: 600;
    font-size: 30px;
    line-height: 1.4;
    text-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* The person's name (wrapped server-side in .ws-name) leads the message and is emphasised so it stands
   apart from the rest of the text. Uses :deep because the message is injected via v-html. */
.ws-message :deep(.ws-name) {
    display: inline-block;
    margin-bottom: 6px;
    font-size: 42px;
    font-weight: 800;
}

.ws-countdown {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
    font-family: 'Sora', sans-serif;
    font-weight: 700;
    font-size: 26px;
    line-height: 1;
}

@keyframes ws-fade {
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* ---------- Password / states ---------- */
.ws-password {
    width: 100%;
    max-width: 420px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
}

.ws-password-label {
    color: #ffffff;
    text-align: center;
    font-size: 16px;
}

.ws-password-error {
    color: #ffe1d6;
    text-align: center;
    font-size: 14px;
}

.ws-button {
    align-self: center;
    border: none;
    border-radius: 12px;
    padding: 12px 32px;
    font-family: 'Sora', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #f65221;
    background: #ffffff;
    cursor: pointer;
    box-shadow: 0 10px 24px -8px rgba(0, 0, 0, 0.35);
}

.ws-state {
    margin-top: 20vh;
    color: #ffffff;
    font-size: 24px;
    text-align: center;
}

.ws-state i {
    font-size: 40px;
}
</style>
